import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { ContactForm } from './contact-form';
import PhoneVerificationStep from './PhoneVerificationStep';
import {
  PageContainer,
  Header,
  MainContent,
  FormSection,
  ImageSection,
} from './styles/contact-form-styles';
import UpdateUserDataIllustration from '../../images/icons/update-userdata-ilustration.svg';
import { useUserProfileContext } from '../../context/UserProfileContext';
import { useHistory } from 'react-router-dom';
import { PhoneVerificationService } from '../../services/phoneVerificationService';

export const UpdateContractInfo = () => {
  const { currentUserProfile, updateProfile } = useUserProfileContext();
  const currentUser = useSelector(state => state.authentication.user);

  // Debug para monitorar alterações no perfil
  React.useEffect(() => {
    console.log('currentUserProfile atualizado:', currentUserProfile);
  }, [currentUserProfile]);

  const [status, setSuccess] = useState({
    success: false,
    error: false,
    phoneError: false,
    companyPhoneError: false,
    companyPhoneErrorMessage: '', // Mensagem de erro adicionada
  });

  // Estados para fluxo de verificação
  const [currentStep, setCurrentStep] = useState('form'); // 'form' ou 'verification'
  const [phoneToVerify, setPhoneToVerify] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('whatsapp');
  const [verificationStatus, setVerificationStatus] = useState({
    isLoading: false,
    error: null,
    success: false,
  });

  const history = useHistory();

  // Ir para tela de verificação (após envio do OTP pelo backend)
  const goToVerificationStep = useCallback(phoneNumber => {
    setPhoneToVerify(phoneNumber);
    setCurrentStep('verification');
    setVerificationStatus({ isLoading: false, error: null, success: false });
  }, []);

  // Reenviar código OTP
  const handleResendCode = useCallback(
    async (method = deliveryMethod) => {
      try {
        setVerificationStatus({ isLoading: true, error: null, success: false });
        await PhoneVerificationService.resendCode(currentUser.getId(), method);
        setVerificationStatus({ isLoading: false, error: null, success: false });
      } catch (error) {
        setVerificationStatus({
          isLoading: false,
          error: error.message || 'Erro ao reenviar código',
          success: false,
        });
      }
    },
    [currentUser]
  );

  // Verificar código OTP (não atualiza perfil de novo)
  const handleCodeVerification = useCallback(
    async code => {
      try {
        setVerificationStatus({ isLoading: true, error: null, success: false });
        await PhoneVerificationService.verifyCode(currentUser.getId(), code);
        setVerificationStatus({ isLoading: false, error: null, success: true });
        setSuccess({
          success: true,
          error: false,
          phoneError: false,
          companyPhoneError: false,
        });
        // Voltar ao formulário após sucesso
        setTimeout(() => {
          setCurrentStep('form');
          setVerificationStatus({ isLoading: false, error: null, success: false });
        }, 2000);
      } catch (error) {
        setVerificationStatus({
          isLoading: false,
          error: error.message || 'Código inválido',
          success: false,
        });
      }
    },
    [currentUser]
  );

  // Estado inicial do formulário
  const [formInitialValues, setFormInitialValues] = useState(currentUserProfile);

  React.useEffect(() => {
    setFormInitialValues(currentUserProfile);
  }, [currentUserProfile]);

  // Voltar ao formulário (trocar número)
  const handleBackToForm = useCallback(() => {
    setCurrentStep('form');
    setVerificationStatus({ isLoading: false, error: null, success: false });
    setSuccess({ success: false, error: false, phoneError: false, companyPhoneError: false });
    setFormInitialValues({ ...currentUserProfile });
  }, [currentUserProfile]);

  // Enviar formulário (atualiza perfil e dispara OTP)
  const onSubmit = useCallback(
    async values => {
      // Resetando o status antes de uma nova tentativa
      setSuccess({
        success: false,
        error: false,
        phoneError: false,
        companyPhoneError: false,
        companyPhoneErrorMessage: '', // Resetando a mensagem de erro
        phoneNumberErrorMessage: '', // Resetando a mensagem de erro
      });

      try {
        // prefer deliveryMethod passed from values over local state
        const method = values.deliveryMethod || deliveryMethod || 'whatsapp';
        const updatedProfile = await updateProfile({
          companyPhone: values.companyPhone,
          phoneNumber: values.phoneNumber,
          deliveryMethod: method,
        });

        // Log para verificar a resposta do backend
        console.log('Resposta do backend:', updatedProfile);

        // Se a API indicar erro no companyPhone
        if (updatedProfile?.code === 1006 || updatedProfile?.data?.companyPhoneError) {
          console.log(
            'Erro no telefone comercial:',
            updatedProfile?.message || COMPANY_PHONE_ERROR_MESSAGE
          );
          setSuccess({
            success: false,
            error: true,
            phoneError: false,
            companyPhoneError: true,
            companyPhoneErrorMessage: updatedProfile?.message || COMPANY_PHONE_ERROR_MESSAGE, // Mensagem do erro
          });
          return; // Não vai para OTP
        }

        // Se houver erro no celular
        if (updatedProfile?.data?.phoneNumberError) {
          console.log('Erro no celular:', PHONE_ERROR_MESSAGE);
          setSuccess({
            success: false,
            error: true,
            phoneError: true,
            companyPhoneError: false,
            phoneNumberErrorMessage: PHONE_ERROR_MESSAGE, // Mensagem do erro
          });
          return; // Não vai para OTP
        }

        // Caso sucesso normal, vai para etapa de OTP
        // ensure parent deliveryMethod is in sync if user changed it in form
        setDeliveryMethod(method);
        goToVerificationStep(values.phoneNumber);
      } catch (error) {
        // Log para erro
        console.error('Erro ao atualizar perfil:', error);
        setSuccess({
          success: false,
          error: true,
          phoneError: error?.data?.phoneNumberError || false,
          companyPhoneError: error?.data?.companyPhoneError || false,
          companyPhoneErrorMessage: error?.data?.companyPhoneErrorMessage || '', // Captura a mensagem do backend
          phoneNumberErrorMessage: error?.data?.phoneNumberErrorMessage || '', // Captura a mensagem do backend
        });
      }
    },
    [updateProfile, goToVerificationStep]
  );

  return (
    <PageContainer>
      <Header>
        <h1>Atualização de dados cadastrais</h1>
      </Header>
      <MainContent>
        <FormSection>
          {currentStep === 'form' ? (
            <ContactForm initialValues={formInitialValues} onSubmit={onSubmit} status={status} />
          ) : (
            <PhoneVerificationStep
              phoneNumber={phoneToVerify}
              deliveryMethod={deliveryMethod}
              onCodeSubmit={handleCodeVerification}
              onResendCode={handleResendCode}
              onChangeNumber={handleBackToForm}
              isLoading={verificationStatus.isLoading}
              error={verificationStatus.error}
              success={verificationStatus.success}
            />
          )}
        </FormSection>
        <ImageSection>
          <img src={UpdateUserDataIllustration} alt="User updating data illustration" />
        </ImageSection>
      </MainContent>
    </PageContainer>
  );
};
